import { KgInsight, KgInsightTakeawaySection, KgInsightThemeType } from '@api';
import { THEME_TAG_COLOR_CLASS_MAP } from '@terminal/modules/reflexivity-graph/kg-insight-constants';
import { KgInsightSection } from '@terminal/views/kg-insight/components/kg-insight-section/KgInsightSection';
import clsx from 'clsx';
import React, { RefCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  findSection,
  takeawayToRows,
} from '../kg-insight-content/utils/kg-insight-content-utils';
import styles from './TopTakeaways.module.scss';

export type ThemeTag = {
  label: string;
  link?: string;
  kind: KgInsightThemeType;
};

export type TopTakeawaysRow = {
  title: string;
  description: string;
  themes: ThemeTag[];
};

export interface TopTakeawaysProps {
  kgInsight: KgInsight;
  ref?: RefCallback<HTMLElement>;
}

export const TopTakeaways = ({ kgInsight, ref }: TopTakeawaysProps) => {
  const { t } = useTranslation(['insights']);
  const takeawaySection = findSection<KgInsightTakeawaySection>(
    kgInsight.sections,
    'takeaway'
  );
  const rows = takeawaySection ? takeawayToRows(takeawaySection) : [];

  return (
    <KgInsightSection
      title={t('insights:catalystGeneration.section_takeaway', {
        count: takeawaySection?.data.cards.length,
      })}
      ref={ref}
      cardsClassName={styles.cards}
    >
      {rows.map((row, index) => (
        <div key={`${row.title}-${index}`} className={styles.row}>
          <span className={styles.index}>{`0${index + 1}`}</span>

          <div className={styles.content}>
            <div className={styles.textBlock}>
              <p className={styles.headline}>{row.title}</p>
              <p className={styles.description}>{row.description}</p>
            </div>

            {row.themes.length > 0 && (
              <p className={styles.themes}>
                {row.themes.map((theme, themeIndex) => {
                  const colorClass =
                    THEME_TAG_COLOR_CLASS_MAP[theme.kind] || styles.themeSoft;

                  return (
                    <React.Fragment key={theme.label}>
                      {themeIndex > 0 && (
                        <span className={styles.themeSoft}>{'•'}</span>
                      )}
                      {theme.link ? (
                        <a
                          href={theme.link}
                          rel="noopener noreferrer"
                          target="_blank"
                          className={clsx(colorClass, styles.themeLink)}
                        >
                          {theme.label}
                        </a>
                      ) : (
                        <span className={colorClass}>{theme.label}</span>
                      )}
                    </React.Fragment>
                  );
                })}
              </p>
            )}
          </div>
        </div>
      ))}
    </KgInsightSection>
  );
};
